import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementsOptionsMode } from '@stripe/stripe-js';
import React from 'react';
import { Link as ReachLink } from 'react-router-dom';
import axios from 'shared/anaxios';
import Button from 'shared/button';
import EmptyState from 'shared/empty-state';
import { Box, Flex, Grid } from 'shared/grid';
import { useParams } from 'react-router';
import Loader from 'shared/loader';
import { formPost, payfastRedirect, paymentMeta } from 'shared/payments';
import settings from 'shared/settings';
import { CroppedText, Heading, Link, Text } from 'shared/typography';
import { toBase58 } from 'shared/utils/b58';
import { currencyFormat } from 'shared/utils/currency';
import { useLocationId } from '../../hooks';
import { useAppSelector } from '../../store';
import { StripeCheckoutForm } from '../booking/pay';
import { BorderBox } from '../shared';
const PayAccount = () => {
  const [paymentData, setPaymentData] = React.useState(null);
  const [openAccordion, setOpenAccordion] = React.useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
  const [locationId, setLocationId] = useLocationId();
  const {
    clientId
  } = useParams();
  const [accountStatus, setAccountStatus] = React.useState('loading');
  const business = useAppSelector(state => state.businesses.data[state.public.businessId]);
  React.useEffect(() => {
    axios.get(`${settings.api2Root}/statement-payment-data/${clientId}`, {
      params: {
        location_id: locationId || undefined
      }
    }).then(({
      data
    }) => {
      setPaymentData(data);
      setAccountStatus('done');
    }).catch(() => {
      setAccountStatus('notfound');
    });
  }, [clientId, locationId]);
  const stripePk = React.useMemo(() => paymentData?.payment_methods?.find(p => p.type === 'stripe' && p.provider_data)?.provider_data?.publishable_key, [paymentData]);
  const stripePromise: Promise<Stripe> | null = React.useMemo(() => {
    if (stripePk) {
      return loadStripe(stripePk);
    }
  }, [stripePk]);
  const {
    total_amount,
    paid_amount,
    total_due,
    currency,
    payment_methods
  } = paymentData || {};
  const stripeOptions: StripeElementsOptionsMode = React.useMemo(() => ({
    mode: 'payment',
    amount: total_due * 100,
    // Stripe expects the amount in cents
    currency: currency?.toLowerCase()
  }), [total_due, currency]);
  if (accountStatus === 'loading') {
    return <Loader />;
  } else if (accountStatus === 'notfound') {
    return <EmptyState heading="Account not found" />;
  }
  return <Grid
  // flexGrow={1}
  gridTemplateAreas={[`
          'summary'
          'methods'
        `, `
          'methods summary'
        `]} gridTemplateColumns={['1fr', '1fr 300px']} data-sentry-element="Grid" data-sentry-component="PayAccount" data-sentry-source-file="component.tsx">
      {total_due > 0 ? <Box gridArea="methods">
          <BorderBox pt={0}>
            {payment_methods.some(p_2 => paymentMeta[p_2.type]?.integrated) ? <>
                <Box px={4} py={8} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.1">
                  <Heading fontSize={2} fontWeight={600}>
                    Choose a payment method
                  </Heading>
                </Box>

                {payment_methods.filter(p_0 => p_0.show_on_invoice).filter(p_1 => paymentMeta[p_1.type]?.integrated).map(({
            id,
            provider_data: {
              form_data,
              host,
              qrcode_url,
              qrcode_svg_url,
              client_id,
              payment_url
            },
            type,
            instructions
          }, idx) => <Box key={idx}>
                        <Grid p={4} gridGap={4} gridTemplateColumns={'auto 1fr auto'} sx={{
              '&:hover, &:active': {
                backgroundColor: 'gray.1'
              },
              cursor: 'pointer'
            }} onClick={() => {
              if (type === 'payfast') {
                setOpenAccordion(null);
                payfastRedirect(host, form_data);
              } else if (type == 'wigwag') {
                formPost(payment_url, {
                  location_id: locationId,
                  client_id: clientId,
                  payment_method_id: id
                });
              } else if (type == 'yoco') {
                formPost(payment_url, {
                  location_id: locationId,
                  client_id: clientId,
                  payment_method_id: id
                });
              } else {
                setOpenAccordion(openAccordion === idx ? null : idx);
              }
            }}>
                          <Flex alignItems="center">
                            {paymentMeta[type].icon}
                          </Flex>

                          <Flex alignItems="center">
                            <CroppedText fontFamily="heading" fontSize={2}>
                              {paymentMeta[type].name}
                            </CroppedText>
                          </Flex>

                          <Flex alignItems="center">
                            {paymentMeta[type].inPage && <FontAwesomeIcon icon={openAccordion === idx ? faChevronDown : faChevronRight} />}
                          </Flex>
                        </Grid>

                        <PayPalScriptProvider options={{
              clientId: client_id,
              currency,
              intent: 'capture'
            }}>
                          {openAccordion === idx && type == 'paypal' && <Box p={4}>
                              <PayPalButtons style={{
                  layout: 'horizontal'
                }} createOrder={(data_0, actions) => {
                  setIsProcessingPayment(true);
                  return actions.order.create({
                    intent: 'CAPTURE',
                    purchase_units: [{
                      amount: {
                        currency_code: currency,
                        value: total_due.toString()
                      }
                    }],
                    application_context: {
                      shipping_preference: 'NO_SHIPPING'
                    }
                  });
                }} onApprove={(details, data_1) => {
                  setIsProcessingPayment(true);
                  return axios.post(`${settings.api2Root}/client-paypal-transaction-complete/${id}`, {
                    order_id: details.orderID,
                    client_id: clientId,
                    location_id: locationId
                  }).then(() => {
                    setTimeout(() => {
                      axios.get(`${settings.api2Root}/statement-payment-data/${clientId}`, {
                        params: {
                          location_id: locationId || undefined
                        }
                      }).then(({
                        data: data_2
                      }) => {
                        setPaymentData(data_2);
                        setIsProcessingPayment(false);
                      });
                    }, 2000);
                  }).catch(() => setIsProcessingPayment(false));
                }} />

                              {/* {isProcessingPayment && <Loader />} */}
                            </Box>}
                        </PayPalScriptProvider>

                        {openAccordion === idx && type == 'stripe' && <Box p={4}>
                            <Elements stripe={stripePromise} options={stripeOptions}>
                              <StripeCheckoutForm paymentMethodId={id} currency={currency} amount={total_due} clientId={clientId} locationId={locationId} />
                            </Elements>
                          </Box>}

                        {openAccordion === idx && type == 'snapscan' && <Box p={4}>
                            <Link href={qrcode_url}>
                              <img alt="qr code" src={qrcode_svg_url} />
                            </Link>
                          </Box>}

                        {openAccordion === idx && type == 'eft' && <Grid p={4}>
                            {instructions.split('\n').map((v, idx_0) => <CroppedText key={idx_0}>{v}</CroppedText>)}
                          </Grid>}
                      </Box>)}
              </> : <EmptyState minHeight="283px" heading={`${business.name} is not accepting online payments at the
                moment.`} callToAction={<Text>
                    Please view your invoice for further instructions or{' '}
                    <Link as={ReachLink} to={`/contact`}>
                      contact us
                    </Link>
                  </Text>} />}
          </BorderBox>
        </Box> : <Box gridArea="methods">
          <BorderBox textAlign="center">
            <CroppedText fontSize={2}>
              Your account has no outstanding invoices. Thank you.
            </CroppedText>
          </BorderBox>
        </Box>}
      <Box gridArea="summary" data-sentry-element="Box" data-sentry-source-file="component.tsx">
        <BorderBox p={0} data-sentry-element="BorderBox" data-sentry-source-file="component.tsx">
          <Box px={4} py={8} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.1" data-sentry-element="Box" data-sentry-source-file="component.tsx">
            <Heading fontSize={2} fontWeight={600} data-sentry-element="Heading" data-sentry-source-file="component.tsx">
              Statement summary
            </Heading>
          </Box>

          {/* <Flex justifyContent="space-between" p={4}>
            <CroppedText>Total</CroppedText>
            <CroppedText>{currencyFormat(total_amount, currency)}</CroppedText>
           </Flex>
           <Flex justifyContent="space-between" p={4}>
            <CroppedText>Paid</CroppedText>
            <CroppedText>{currencyFormat(paid_amount, currency)}</CroppedText>
           </Flex> */}

          <Flex justifyContent="space-between" px={4} py={8}
        // borderTopWidth="1px"
        // borderTopStyle="solid"
        // borderTopColor="gray.1"
        data-sentry-element="Flex" data-sentry-source-file="component.tsx">
            <CroppedText fontWeight={600} data-sentry-element="CroppedText" data-sentry-source-file="component.tsx">Total due</CroppedText>
            <CroppedText fontWeight={600} data-sentry-element="CroppedText" data-sentry-source-file="component.tsx">
              {currencyFormat(total_due, currency)}
            </CroppedText>
          </Flex>

          <Box p={4} borderTopWidth="1px" borderTopStyle="solid" borderTopColor="gray.1" data-sentry-element="Box" data-sentry-source-file="component.tsx">
            <Button as="a" variant="outlined" color="gray" rel="noopener noreferrer" target="_blank" href={settings.bypassPWAScope(`/s/${toBase58(clientId)}?download=false` + (locationId ? `&location_id=${locationId}` : ''))} data-sentry-element="Button" data-sentry-source-file="component.tsx">
              View statement
            </Button>
          </Box>
        </BorderBox>
      </Box>
    </Grid>;
};
export default PayAccount;